<template>
  <div class="plasmid-database">
    <div class="content-section">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-wrapper">
          <el-input
            v-model="searchQuery"
            placeholder="Search by species or plasmid name..."
            :prefix-icon="Search"
            clearable
            @input="handleSearch"
            class="search-input"
          >
            <template #append>
              <el-button :icon="Search" @click="handleSearch">
                Search
              </el-button>
            </template>
          </el-input>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h2>Plasmid Data</h2>
          <div class="table-actions">
            <el-select v-model="pageSize" @change="handleSizeChange" class="page-size-select">
              <el-option
                v-for="size in [10, 20, 50, 100]"
                :key="size"
                :label="size + ' items per page'"
                :value="size"
              />
            </el-select>
          </div>
        </div>

        <el-table
          v-loading="loading"
          :data="plasmids"
          style="width: 100%"
          border
          stripe
          highlight-current-row
          class="custom-table"
        >
          <el-table-column
            type="index"
            label="#"
            width="60"
            align="center"
          />
          <el-table-column
            prop="species_name"
            label="Species Name"
            sortable
            min-width="310"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="species-name">
                <span class="species-text">{{ scope.row.species_name }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="plasmid"
            label="Plasmid"
            sortable
            width="180"
            show-overflow-tooltip
          >
            <template #default="scope">
              <span>{{ scope.row.plasmid }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="size"
            label="Size (bp)"
            sortable
            width="120"
            align="right"
          >
            <template #default="scope">
              <span class="size-value">{{ formatNumber(scope.row.size) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="shape"
            label="Shape"
            width="100"
            align="center"
          >
            <template #default="scope">
              <el-tag
                :type="scope.row.shape === 'circular' ? 'success' : 'warning'"
                effect="light"
                class="shape-tag"
              >
                {{ scope.row.shape }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="Actions"
            width="200"
            fixed="right"
            align="center"
          >
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  :icon="View"
                  circle
                  @click="viewFasta(scope.row)"
                />
                <el-button
                  type="success"
                  :icon="Download"
                  circle
                  @click="showDownloadDialog(scope.row)"
                />
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            background
          />
        </div>
      </div>

      <!-- FASTA查看对话框 -->
      <el-dialog
        v-model="detailsDialogVisible"
        width="90%"
        class="plasmid-dialog"
        :fullscreen="true"
        destroy-on-close
      >
        <div v-if="selectedPlasmid" class="plasmid-details">
          <div class="detail-section">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="Species">{{ selectedPlasmid.species_name }}</el-descriptions-item>
              <el-descriptions-item label="Plasmid">{{ selectedPlasmid.plasmid }}</el-descriptions-item>
              <el-descriptions-item label="Size (bp)">{{ formatNumber(selectedPlasmid.size) }}</el-descriptions-item>
              <el-descriptions-item label="Shape">{{ selectedPlasmid.shape }}</el-descriptions-item>
              <el-descriptions-item label="GC Content">{{ gcContent }}%</el-descriptions-item>
            </el-descriptions>
          </div>

          <el-tabs v-model="activeTab" class="sequence-tabs" @tab-click="handleTabClick">
            <el-tab-pane label="Sequence Text" name="text">
              <div class="sequence-section">
                <h3>FASTA Sequence</h3>
                <div v-loading="loadingSequence" class="sequence-content">
                  <pre>{{ fastaContent }}</pre>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Plasmid Map" name="map">
              <div class="sequence-section">
                <div class="map-controls">
                  <el-radio-group v-model="plotType" @change="handlePlotTypeChange" size="large">
                    <el-radio-button label="circular">Circular</el-radio-button>
                    <el-radio-button label="linear">Linear</el-radio-button>
                  </el-radio-group>
                </div>
                <div class="visualization-wrapper">
                  <div v-show="activeTab === 'map'" class="plasmid-map">
                    <div class="map-zoom-controls">
                      <el-button-group>
                        <el-button :icon="ZoomIn" @click="zoomIn" title="放大"></el-button>
                        <el-button :icon="ZoomOut" @click="zoomOut" title="缩小"></el-button>
                        <el-button :icon="RefreshRight" @click="resetZoom" title="重置"></el-button>
                      </el-button-group>
                    </div>
                    <div 
                      v-if="plasmidMapUrl" 
                      class="plasmid-map-container" 
                      @mousedown="startDrag" 
                      @mousemove="onDrag" 
                      @mouseup="stopDrag" 
                      @mouseleave="stopDrag"
                      @wheel.prevent="handleWheel"
                    >
                      <img 
                        :src="plasmidMapUrl" 
                        :alt="`${plotType === 'circular' ? '环形' : '条形'}质粒图`" 
                        class="plasmid-map-image" 
                        :style="{
                          transform: `scale(${zoomLevel}) translate(${positionX}px, ${positionY}px)`,
                          cursor: isDragging ? 'grabbing' : 'grab'
                        }" 
                      />
                    </div>
                    <div v-else class="no-map-message">
                      Loading plasmid map...
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Sequence Features" name="features">
              <div class="sequence-section">
                <h3>Restriction Sites</h3>
                <el-scrollbar>
                  <el-table
                    :data="restrictionSites"
                    border
                    style="width: 100%"
                    height="calc(100vh - 400px)"
                  >
                    <el-table-column prop="enzyme" label="Enzyme" width="180" fixed />
                    <el-table-column prop="site" label="Recognition Site" width="180" />
                    <el-table-column prop="positions" label="Positions" min-width="200">
                      <template #default="scope">
                        {{ scope.row.positions }}
                      </template>
                    </el-table-column>
                  </el-table>
                </el-scrollbar>
              </div>
            </el-tab-pane>

            <!-- 穿梭质粒标签页 - 仅对特定质粒显示 -->
            <el-tab-pane 
              v-if="isShuttlePlasmid(selectedPlasmid?.plasmid)" 
              label="Shuttle Plasmids" 
              name="shuttle"
            >
              <div class="sequence-section">
                <div class="shuttle-plasmid-content">
                  <div v-if="selectedPlasmid?.plasmid === 'pC2A'">
                    <h3>Natural Plasmid</h3>
                    <p>pC2A</p>
                    
                    <h3>Shuttle Plasmids</h3>
                    <ul>
                      <li>pWM307</li>
                      <li>pWM309</li>
                      <li>pWM311</li>
                      <li>pWM313</li>
                      <li>pWM315</li>
                      <li>pWM317</li>
                      <li>pWM319</li>
                      <li>pWM321</li>
                      <li>pRS1550</li>
                    </ul>
                    
                    <h3>Methanogenic Archaea Hosts</h3>
                    <ul>
                      <li>Methanosarcina mazei</li>
                      <li>Methanosarcina acetivorans</li>
                    </ul>
                  </div>
                  
                  <div v-else-if="selectedPlasmid?.plasmid === 'pURB500'">
                    <h3>Natural Plasmid</h3>
                    <p>pURB500</p>
                    
                    <h3>Shuttle Plasmids</h3>
                    <ul>
                      <li>pDLT44</li>
                      <li>pWLG30</li>
                      <li>pWLG40</li>
                      <li>pAW42</li>
                    </ul>
                    
                    <h3>Methanogenic Archaea Hosts</h3>
                    <ul>
                      <li>Methanococcus maripaludis</li>
                    </ul>
                  </div>
                  
                  <div v-else-if="selectedPlasmid?.plasmid === 'pME2001'">
                    <h3>Natural Plasmid</h3>
                    <p>pME2001</p>
                    
                    <h3>Shuttle Plasmids</h3>
                    <ul>
                      <li>pMVS</li>
                    </ul>
                    
                    <h3>Methanogenic Archaea Hosts</h3>
                    <ul>
                      <li>Methanothermobacter thermautotrophicus</li>
                    </ul>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="handleDialogClose">Close</el-button>
            <el-button type="primary" @click="downloadFasta(selectedPlasmid)">
              Download FASTA
            </el-button>
            <el-button type="success" @click="downloadGB(selectedPlasmid)">
              Download GenBank
            </el-button>
          </div>
        </template>
      </el-dialog>

      <!-- 添加下载选择对话框 -->
      <el-dialog
        v-model="downloadDialogVisible"
        title="Select Download Format"
        width="30%"
        center
      >
        <div class="download-options">
          <el-checkbox v-model="downloadOptions.fasta">FASTA Format</el-checkbox>
          <el-checkbox v-model="downloadOptions.gb">GenBank Format</el-checkbox>
        </div>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="downloadDialogVisible = false">Cancel</el-button>
            <el-button type="primary" @click="handleDownload">
              Download
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { Search, View, Download, ZoomIn, ZoomOut, RefreshRight } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

export default {
  name: 'PlasmidDatabase',
  setup() {
    const loading = ref(false)
    const searchQuery = ref('')
    const plasmids = ref([])
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const detailsDialogVisible = ref(false)
    const selectedPlasmid = ref(null)
    const loadingSequence = ref(false)
    const fastaContent = ref('')
    const activeTab = ref('text')
    const gcContent = ref('')
    const restrictionSites = ref([])
    const plasmidMapUrl = ref(null)
    const plotType = ref('circular') // 默认为环形图
    const downloadDialogVisible = ref(false)
    const downloadOptions = ref({
      fasta: false,
      gb: false
    })
    const currentPlasmid = ref(null)
    
    // 图片缩放和移动相关变量
    const zoomLevel = ref(1) // 缩放级别，1为原始大小
    const positionX = ref(0) // X轴位移
    const positionY = ref(0) // Y轴位移
    const isDragging = ref(false) // 是否正在拖动
    const dragStartX = ref(0) // 拖动开始时的X坐标
    const dragStartY = ref(0) // 拖动开始时的Y坐标
    const lastPositionX = ref(0) // 上次的X位移
    const lastPositionY = ref(0) // 上次的Y位移

    // 加载数据
    const loadData = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/plasmid-data/', {
          params: {
            page: currentPage.value,
            page_size: pageSize.value,
            search: searchQuery.value
          }
        })
        plasmids.value = response.data.items
        total.value = response.data.total
      } catch (error) {
        console.error('Error loading plasmids:', error)
        ElMessage.error('Failed to load plasmids')
      } finally {
        loading.value = false
      }
    }

    // 查看FASTA序列
    const viewFasta = async (plasmid) => {
      selectedPlasmid.value = plasmid
      detailsDialogVisible.value = true
      loadingSequence.value = true
      fastaContent.value = ''
      plasmidMapUrl.value = null
      restrictionSites.value = []

      try {
        const response = await axios.get(`/api/plasmid/${plasmid.plasmid}/fasta/`, {
          params: {
            species_name: plasmid.species_name
          }
        })
        if (response.data && response.data.content) {
          fastaContent.value = response.data.content
          const sequence = response.data.content.split('\n').slice(1).join('')
          
          // 计算GC含量
          const gc = (sequence.match(/[GC]/gi) || []).length
          gcContent.value = ((gc / sequence.length) * 100).toFixed(2)
          
          // 查找限制性内切酶位点
          findRestrictionSites(sequence)
          
          // 加载质粒图
          if (activeTab.value === 'map') {
            loadPlasmidMap()
          }
        } else {
          throw new Error('Invalid response format')
        }
      } catch (error) {
        console.error('Error loading FASTA sequence:', error)
        ElMessage.error('Failed to load FASTA sequence')
        fastaContent.value = 'Failed to load sequence data'
      } finally {
        loadingSequence.value = false
      }
    }

    // 加载质粒图
    const loadPlasmidMap = async () => {
      if (!selectedPlasmid.value) return
      
      try {
        // 如果已有URL，先释放它
        if (plasmidMapUrl.value) {
          URL.revokeObjectURL(plasmidMapUrl.value)
          plasmidMapUrl.value = null
        }
        
        // 重置缩放和位置状态
        resetZoom()
        
        const response = await axios.get(`/api/plasmid/${selectedPlasmid.value.plasmid}/map/`, {
          params: {
            species_name: selectedPlasmid.value.species_name,
            plot_type: plotType.value // 传递图形类型参数
          },
          responseType: 'blob'
        })
        
        const url = URL.createObjectURL(response.data)
        plasmidMapUrl.value = url
      } catch (error) {
        console.error('Error loading plasmid map:', error)
        ElMessage.error(`加载${plotType.value === 'circular' ? '环形' : '条形'}质粒图失败`)
      }
    }

    // 下载FASTA文件
    const downloadFasta = async (plasmid) => {
      try {
        const response = await axios.get(`/api/plasmid/${plasmid.plasmid}/download/`, {
          params: {
            species_name: plasmid.species_name
          },
          responseType: 'blob'
        })
        
        // 检查内容类型
        const contentType = response.headers['content-type'] || 'text/plain';
        console.log('FASTA下载内容类型:', contentType);
        
        // 对于文本内容，尝试将二进制转为文本
        if (contentType.includes('text/plain')) {
          try {
            // 创建FileReader来读取blob
            const reader = new FileReader();
            reader.onload = function() {
              // 创建含文本内容的Blob
              const textBlob = new Blob([reader.result], { type: 'text/plain' });
              
              // 下载文件
              const url = URL.createObjectURL(textBlob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `${plasmid.species_name}_${plasmid.plasmid}.fasta`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            };
            // 读取为文本
            reader.readAsText(response.data);
          } catch (error) {
            console.error('读取文本时出错:', error);
            // 如果转换失败，回退到直接下载二进制
            downloadBinary(response.data, `${plasmid.species_name}_${plasmid.plasmid}.fasta`);
          }
        } else {
          // 直接下载二进制
          downloadBinary(response.data, `${plasmid.species_name}_${plasmid.plasmid}.fasta`);
        }
      } catch (error) {
        ElMessage.error('下载FASTA文件失败');
        console.error('Error downloading FASTA:', error);
      }
    }
    
    // 二进制下载辅助函数
    const downloadBinary = (data, filename) => {
      const blob = new Blob([data], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    
    // 添加下载GB文件的函数
    const downloadGB = async (plasmid) => {
      try {
        const response = await axios.get(`/api/plasmid/${plasmid.plasmid}/gb/`, {
          params: {
            species_name: plasmid.species_name
          },
          responseType: 'blob'
        })
        
        // 检查内容类型
        const contentType = response.headers['content-type'] || 'text/plain';
        console.log('GenBank下载内容类型:', contentType);
        
        // 对于文本内容，尝试将二进制转为文本
        if (contentType.includes('text/plain')) {
          try {
            // 创建FileReader来读取blob
            const reader = new FileReader();
            reader.onload = function() {
              // 创建含文本内容的Blob
              const textBlob = new Blob([reader.result], { type: 'text/plain' });
              
              // 下载文件
              const url = URL.createObjectURL(textBlob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `${plasmid.species_name}_${plasmid.plasmid}.gb`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            };
            // 读取为文本
            reader.readAsText(response.data);
          } catch (error) {
            console.error('读取文本时出错:', error);
            // 如果转换失败，回退到直接下载二进制
            downloadBinary(response.data, `${plasmid.species_name}_${plasmid.plasmid}.gb`);
          }
        } else {
          // 直接下载二进制
          downloadBinary(response.data, `${plasmid.species_name}_${plasmid.plasmid}.gb`);
        }
      } catch (error) {
        ElMessage.error('下载GenBank文件失败');
        console.error('Error downloading GenBank:', error);
      }
    }

    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1
      loadData()
    }

    // 处理页面大小变化
    const handleSizeChange = (val) => {
      pageSize.value = val
      currentPage.value = 1
      loadData()
    }

    // 处理页码变化
    const handleCurrentChange = (val) => {
      currentPage.value = val
      loadData()
    }

    // 格式化数字
    const formatNumber = (num) => {
      return num ? num.toLocaleString() : '-'
    }

    // 添加标签页点击处理函数
    const handleTabClick = (tab) => {
      if (tab.props.name === 'map' && selectedPlasmid.value) {
        loadPlasmidMap()
      }
    }
    
    // 处理图形类型变更
    const handlePlotTypeChange = () => {
      // 重置缩放和位置状态
      resetZoom()
      
      if (activeTab.value === 'map' && selectedPlasmid.value) {
        loadPlasmidMap()
      }
    }

    // 添加对话框关闭处理函数
    const handleDialogClose = () => {
      if (plasmidMapUrl.value) {
        URL.revokeObjectURL(plasmidMapUrl.value)
      }
      // 重置缩放和位置状态
      resetZoom()
      activeTab.value = 'text'
      detailsDialogVisible.value = false
    }

    // 查找限制性内切酶位点
    const findRestrictionSites = (sequence) => {
      const sites = {
        'EcoRI': 'GAATTC',
        'BamHI': 'GGATCC',
        'HindIII': 'AAGCTT',
        'XhoI': 'CTCGAG',
        'NdeI': 'CATATG'
      }
      
      restrictionSites.value = Object.entries(sites).map(([enzyme, site]) => {
        const positions = []
        let pos = sequence.toUpperCase().indexOf(site)
        while (pos !== -1) {
          positions.push(pos + 1)  // 转换为1-based位置
          pos = sequence.toUpperCase().indexOf(site, pos + 1)
        }
        return {
          enzyme,
          site,
          positions: positions.length > 0 ? positions.join(', ') : 'None'
        }
      }).filter(site => site.positions !== 'None')
    }

    // 显示下载对话框
    const showDownloadDialog = (plasmid) => {
      currentPlasmid.value = plasmid
      downloadOptions.value.fasta = false
      downloadOptions.value.gb = false
      downloadDialogVisible.value = true
    }

    // 处理下载
    const handleDownload = async () => {
      if (!downloadOptions.value.fasta && !downloadOptions.value.gb) {
        ElMessage.warning('请至少选择一种格式')
        return
      }

      try {
        if (downloadOptions.value.fasta) {
          await downloadFasta(currentPlasmid.value)
        }
        
        if (downloadOptions.value.gb) {
          await downloadGB(currentPlasmid.value)
        }
        
        downloadDialogVisible.value = false;
        ElMessage.success('下载成功');
      } catch (error) {
        ElMessage.error('下载失败，请稍后重试');
        console.error('下载错误:', error);
      }
    }

    // 放大图片
    const zoomIn = () => {
      zoomLevel.value = Math.min(zoomLevel.value + 0.2, 5) // 限制最大缩放为5倍
    }
    
    // 缩小图片
    const zoomOut = () => {
      zoomLevel.value = Math.max(zoomLevel.value - 0.2, 0.5) // 限制最小缩放为0.5倍
    }
    
    // 重置缩放和位置
    const resetZoom = () => {
      zoomLevel.value = 1
      positionX.value = 0
      positionY.value = 0
    }
    
    // 开始拖动
    const startDrag = (event) => {
      isDragging.value = true
      dragStartX.value = event.clientX
      dragStartY.value = event.clientY
      lastPositionX.value = positionX.value
      lastPositionY.value = positionY.value
    }
    
    // 拖动中
    const onDrag = (event) => {
      if (!isDragging.value) return
      
      // 计算移动距离，除以缩放级别使移动距离与缩放级别成反比
      const dx = (event.clientX - dragStartX.value) / zoomLevel.value
      const dy = (event.clientY - dragStartY.value) / zoomLevel.value
      
      positionX.value = lastPositionX.value + dx
      positionY.value = lastPositionY.value + dy
    }
    
    // 停止拖动
    const stopDrag = () => {
      isDragging.value = false
    }
    
    // 鼠标滚轮缩放
    const handleWheel = (event) => {
      // 向下滚动为负值，向上滚动为正值
      const delta = -Math.sign(event.deltaY) * 0.1
      const newZoom = Math.max(0.5, Math.min(5, zoomLevel.value + delta))
      
      // 计算鼠标位置相对于图片容器的偏移量
      const container = event.currentTarget.getBoundingClientRect()
      const mouseX = event.clientX - container.left
      const mouseY = event.clientY - container.top
      
      // 计算鼠标位置在图片上的相对坐标（考虑当前缩放和位移）
      const imgX = (mouseX - positionX.value * zoomLevel.value) / zoomLevel.value
      const imgY = (mouseY - positionY.value * zoomLevel.value) / zoomLevel.value
      
      // 计算新的位移，保持鼠标指向的图片点不变
      if (zoomLevel.value !== newZoom) {
        positionX.value = mouseX / newZoom - imgX
        positionY.value = mouseY / newZoom - imgY
      }
      
      zoomLevel.value = newZoom
    }
    
    // 判断是否为穿梭质粒
    const isShuttlePlasmid = (plasmidName) => {
      const shuttlePlasmids = ['pC2A', 'pURB500', 'pME2001']
      return shuttlePlasmids.includes(plasmidName)
    }
    
    onMounted(() => {
      loadData()
    })

    return {
      loading,
      loadingSequence,
      searchQuery,
      plasmids,
      currentPage,
      pageSize,
      total,
      detailsDialogVisible,
      selectedPlasmid,
      fastaContent,
      activeTab,
      gcContent,
      restrictionSites,
      plasmidMapUrl,
      plotType,
      // 缩放和拖动相关变量
      zoomLevel,
      positionX,
      positionY,
      isDragging,
      // 图标
      Search,
      View,
      Download,
      ZoomIn,
      ZoomOut,
      RefreshRight,
      // 方法
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      viewFasta,
      downloadFasta,
      formatNumber,
      handleTabClick,
      handleDialogClose,
      handlePlotTypeChange,
      downloadGB,
      showDownloadDialog,
      downloadDialogVisible,
      downloadOptions,
      handleDownload,
      // 缩放和拖动相关方法
      zoomIn,
      zoomOut,
      resetZoom,
      startDrag,
      onDrag,
      stopDrag,
      handleWheel,
      // 穿梭质粒判断函数
      isShuttlePlasmid
    }
  }
}
</script>

<style scoped>
.plasmid-database {
  padding: 20px;
  max-width: 1280px;
  margin: 0 auto;
}

.content-section {
  margin-top: 77px;
}

.search-section {
  margin-bottom: 30px;
}

.search-wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
}

.table-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1a1a1a;
}

.custom-table {
  margin-bottom: 20px;
}

.species-name {
  display: flex;
  align-items: center;
}

.species-text {
  color: #1a1a1a;
  font-weight: 500;
}

.size-value {
  font-family: 'Consolas', 'Courier New', monospace;
  color: #1a1a1a;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.shape-tag {
  text-transform: capitalize;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.plasmid-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.plasmid-details {
  padding: 20px;
}

.detail-section {
  margin-bottom: 20px;
}

.sequence-section {
  margin-top: 20px;
  height: calc(100vh - 300px);
  display: flex;
  flex-direction: column;
}

.sequence-section h3 {
  margin-bottom: 16px;
}

.sequence-content {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 4px;
  height: calc(100vh - 300px);
  overflow-y: auto;
  margin: 20px 0;
}

.sequence-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}

.dialog-footer {
  padding: 20px;
  text-align: right;
  background: #fff;
  border-top: 1px solid #e4e7ed;
}

.sequence-tabs {
  margin-top: 20px;
}

.map-controls {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.visualization-wrapper {
  width: 100%;
  height: 700px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 20px;
  position: relative;
}

.plasmid-map {
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.map-zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  padding: 5px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.plasmid-map-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plasmid-map-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
  transition: transform 0.05s ease-out;
  user-select: none;
  -webkit-user-drag: none;
}

.no-map-message {
  color: #909399;
  font-size: 16px;
}

:deep(.el-scrollbar__wrap) {
  overflow-x: hidden;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

.download-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 20px;
}

.download-options .el-checkbox {
  margin-right: 0;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }

  .table-header {
    flex-direction: column;
    gap: 15px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }

  .visualization-wrapper {
    height: 400px;
  }
}
</style>